import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { apiFetch } from '../../utils/api';
import QuoteDetail from '../../components/admin/QuoteDetail';
import './AdminQuotesPage.css';

// const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const STATUS_COLORS = {
  new:         { bg: 'rgba(255,183,206,0.12)', border: 'rgba(255,183,206,0.35)', text: '#FFB7CE' },
  contacted:   { bg: 'rgba(100,180,255,0.12)', border: 'rgba(100,180,255,0.35)', text: '#64b4ff' },
  in_progress: { bg: 'rgba(120,220,160,0.12)', border: 'rgba(120,220,160,0.35)', text: '#78dca0' },
  closed:      { bg: 'rgba(255,255,255,0.06)', border: 'rgba(255,255,255,0.12)', text: 'rgba(255,255,255,0.35)' },
}

const STATUS_LABELS = {
  new: 'New', contacted: 'Contacted', in_progress: 'In Progress', closed: 'Closed',
}

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-KE', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function initials(name) {
  return name?.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?'
}

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.new
  return (
    <span className='aqp-badge' style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text }}>
      {STATUS_LABELS[status] || status}
    </span>
  )
}

export default function AdminQuotesPage() {
  const [quotes, setQuotes]           = useState([])
  const [loading, setLoading]         = useState(true)
  const [error, setError]             = useState(null)
  const [selected, setSelected]       = useState(null)
  const [search, setSearch]           = useState('')
  const [filterStatus, setFilter]     = useState('all')
  const [projectPrompt, setPrompt]    = useState(null) // quote to convert
  const navigate                      = useNavigate()

  useEffect(() => {
    apiFetch('/api/quotes')
      .then(r => r.json())
      .then(d => setQuotes(d.quotes || []))
      .catch(() => setError('Could not load quotes. Is the backend running?'))
      .finally(() => setLoading(false))
  }, [])

  const handleStatusChange = (id, newStatus) => {
    setQuotes(prev => prev.map(q => q.id === id ? { ...q, status: newStatus } : q))
    if (selected?.id === id) setSelected(prev => ({ ...prev, status: newStatus }))
    if (newStatus === 'closed') {
      const quote = quotes.find(q => q.id === id)
      if (quote) setPrompt(quote)
    }
  }

  const counts = quotes.reduce((acc, q) => {
    acc[q.status] = (acc[q.status] || 0) + 1
    acc.total = (acc.total || 0) + 1
    return acc
  }, {})

  const filtered = quotes.filter(q => {
    const matchStatus = filterStatus === 'all' || q.status === filterStatus
    const term = search.toLowerCase()
    const matchSearch = !term || [q.name, q.email, q.company, q.services]
      .some(f => f?.toLowerCase().includes(term))
    return matchStatus && matchSearch
  })

  return (
    <div className='aqp-shell'>
      {/* Sidebar — no filter props, clean */}
      <AdminSidebar counts={counts} />

      <main className='aqp-main'>
        {/* Topbar */}
        <div className='aqp-topbar'>
          <div>
            <h1 className='aqp-title'>Quote Requests</h1>
            <p className='aqp-sub'>{filtered.length} of {quotes.length} submissions</p>
          </div>
          <div className='aqp-search-wrap'>
            <svg className='aqp-search-icon' viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className='aqp-search'
              type='text'
              placeholder='Search name, email, service…'
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* ── Horizontal status filter bar ── */}
        <div className='aqp-filter-bar'>
          <button
            className={`aqp-filter-btn ${filterStatus === 'all' ? 'aqp-filter-btn--active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
            <span className='aqp-filter-count'>{counts.total || 0}</span>
          </button>
          {Object.entries(STATUS_LABELS).map(([key, label]) => {
            const c = STATUS_COLORS[key]
            return (
              <button
                key={key}
                className={`aqp-filter-btn ${filterStatus === key ? 'aqp-filter-btn--active' : ''}`}
                style={filterStatus === key ? {
                  background: c.bg,
                  borderColor: c.border,
                  color: c.text,
                } : {}}
                onClick={() => setFilter(filterStatus === key ? 'all' : key)}
              >
                <span className='aqp-filter-dot' style={{ background: c.text }}></span>
                {label}
                <span className='aqp-filter-count'>{counts[key] || 0}</span>
              </button>
            )
          })}
        </div>

        {/* Loading */}
        {loading && (
          <div className='aqp-state'>
            <div className='aqp-spinner'></div>
            <p>Loading quotes…</p>
          </div>
        )}

        {/* Error */}
        {error && <div className='aqp-state aqp-state--error'><p>⚠ {error}</p></div>}

        {/* Empty */}
        {!loading && !error && filtered.length === 0 && (
          <div className='aqp-state'>
            <p>{search || filterStatus !== 'all' ? 'No quotes match your filter.' : 'No quotes submitted yet.'}</p>
          </div>
        )}

        {/* Table */}
        {!loading && !error && filtered.length > 0 && (
          <div className='aqp-table'>
            <div className='aqp-table-header'>
              <span>Client</span>
              <span>Services</span>
              <span>Budget</span>
              <span>Timeline</span>
              <span>Submitted</span>
              <span>Status</span>
            </div>

            {filtered.map((q, i) => (
              <button
                key={q.id}
                className='aqp-row'
                onClick={() => setSelected(q)}
                style={{ animationDelay: `${i * 0.035}s` }}
              >
                <div className='aqp-row-client'>
                  <div className='aqp-avatar'>{initials(q.name)}</div>
                  <div>
                    <p className='aqp-row-name'>{q.name}</p>
                    <p className='aqp-row-email'>{q.email}</p>
                  </div>
                </div>

                <div className='aqp-row-services'>
                  {(q.services || '').split(',').map(s => s.trim()).filter(Boolean).slice(0, 2).map(s => (
                    <span key={s} className='aqp-chip'>{s}</span>
                  ))}
                  {(q.services || '').split(',').filter(Boolean).length > 2 && (
                    <span className='aqp-chip aqp-chip--more'>
                      +{(q.services || '').split(',').filter(Boolean).length - 2}
                    </span>
                  )}
                </div>

                <span className='aqp-row-budget'>{q.budget}</span>

                <span className={`aqp-row-timeline ${q.timeline?.includes('Rush') ? 'aqp-timeline--rush' : ''}`}>
                  {q.timeline?.replace(' (Rush — extra cost applies)', ' ⚡')}
                </span>

                <span className='aqp-row-date'>{formatDate(q.submitted_at)}</span>

                <StatusBadge status={q.status} />
              </button>
            ))}
          </div>
        )}
      </main>

      {selected && (
        <QuoteDetail
          quote={selected}
          onClose={() => setSelected(null)}
          onStatusChange={handleStatusChange}
        />
      )}

      {/* Add to Projects prompt */}
      {projectPrompt && (
        <div className='aqp-prompt-overlay' onClick={() => setPrompt(null)}>
          <div className='aqp-prompt' onClick={e => e.stopPropagation()}>
            <div className='aqp-prompt-icon'>🎉</div>
            <h3>Quote closed!</h3>
            <p>
              Would you like to add <strong>{projectPrompt.name}</strong>'s
              project to your Projects page? It'll pre-fill their details
              so you just need to add the price and dates.
            </p>
            <div className='aqp-prompt-actions'>
              <button
                className='aqp-prompt-skip'
                onClick={() => setPrompt(null)}
              >
                Not now
              </button>
              <button
                className='aqp-prompt-go'
                onClick={() => {
                  // Pass quote data via sessionStorage so ProjectsPage can pre-fill
                  sessionStorage.setItem('prefill_project', JSON.stringify({
                    quote_id:       projectPrompt.id,
                    client_name:    projectPrompt.name,
                    client_email:   projectPrompt.email,
                    client_company: projectPrompt.company || '',
                    services:       projectPrompt.services || '',
                    title:          `${projectPrompt.name} — ${projectPrompt.services || 'Project'}`,
                    description:    projectPrompt.requirement || '',
                  }))
                  setPrompt(null)
                  navigate('/admin/projects')
                }}
              >
                Yes, add to Projects →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}